import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import type { ConfirmationResult } from 'firebase/auth';

export default function Login() {
  const { login, verifyCode } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [confirmResult, setConfirmResult] = useState<ConfirmationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [isContractor, setIsContractor] = useState(false);

  const handleSendCode = async () => {
    if (!phone || phone.length < 10) {
      toast.error('Enter a valid phone number');
      return;
    }
    setLoading(true);
    try {
      const formattedPhone = phone.startsWith('+') ? phone : `+1${phone.replace(/\D/g, '')}`;
      const result = await login(formattedPhone);
      setConfirmResult(result);
      toast.success('Verification code sent!');
    } catch (err: any) {
      toast.error(err.message || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!confirmResult || !code) return;
    setLoading(true);
    try {
      await verifyCode(confirmResult, code);
      toast.success('Welcome to WorkDoe!');
      navigate(isContractor ? '/onboarding/contractor' : '/onboarding/homeowner');
    } catch (err: any) {
      toast.error(err.message || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '100vh', padding: '32px',
      background: 'var(--color-warm-white)',
    }}>
      <img src="/assets/logo-icon.svg" alt="WorkDoe" style={{ width: 80, height: 80, marginBottom: 24 }} />
      <h1 style={{ fontSize: '1.8rem', fontWeight: 700, color: 'var(--color-dark-brown)', marginBottom: 8 }}>
        Welcome to WorkDoe
      </h1>
      <p style={{ color: 'var(--color-dark-brown)', opacity: 0.7, marginBottom: 32 }}>
        Sign in with your phone number
      </p>

      {/* Role toggle */}
      <div className="chip-group" style={{ marginBottom: 24 }}>
        <button className={`chip ${!isContractor ? 'active' : ''}`} onClick={() => setIsContractor(false)}>
          I Need Help
        </button>
        <button className={`chip ${isContractor ? 'active' : ''}`} onClick={() => setIsContractor(true)}>
          I'm a Contractor
        </button>
      </div>

      <div style={{ width: '100%', maxWidth: 380 }}>
        {!confirmResult ? (
          <>
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              style={{
                width: '100%', padding: '14px 16px', borderRadius: 'var(--radius-md)',
                border: '2px solid var(--color-tan)', fontFamily: 'var(--font-family)',
                fontSize: '1.1rem', marginBottom: 16, background: 'var(--color-cream)',
              }}
            />
            <button className="btn-primary" onClick={handleSendCode} disabled={loading} style={{ width: '100%' }}>
              {loading ? 'Sending...' : 'Send Verification Code'}
            </button>
          </>
        ) : (
          <>
            <input
              type="text"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 6-digit code"
              maxLength={6}
              style={{
                width: '100%', padding: '14px 16px', borderRadius: 'var(--radius-md)',
                border: '2px solid var(--color-tan)', fontFamily: 'var(--font-family)',
                fontSize: '1.4rem', textAlign: 'center', letterSpacing: '0.3em',
                marginBottom: 16, background: 'var(--color-cream)',
              }}
            />
            <button className="btn-primary" onClick={handleVerify} disabled={loading} style={{ width: '100%' }}>
              {loading ? 'Verifying...' : 'Verify & Continue'}
            </button>
          </>
        )}
      </div>

      <div id="recaptcha-container" />

      <p style={{ marginTop: 32, fontSize: '0.8rem', color: 'var(--color-dark-brown)', opacity: 0.5, textAlign: 'center', maxWidth: 400 }}>
        By signing in, you agree to our <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>. WorkDoe is a matching platform only.
      </p>
    </div>
  );
}
